import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  ChevronRight,
  Search,
  CheckCircle2,
  Wallet,
  AlertCircle,
  FileText,
  Filter,
  Bell,
} from "lucide-react";
import { listCustomers, getAppSettings } from "@/lib/api";
import { sendBalanceReminder } from "@/lib/share";
import { Input } from "@/components/ui/input";

const fmtMoney = (n) => `€${(n || 0).toFixed(2)}`;

// Visual pill keyed off the backend `status` field — see /api/customers.
const StatusPill = ({ status, balance, deposits }) => {
  const base =
    "inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-semibold";
  if (status === "paid") {
    return (
      <span
        className={`${base} bg-[#16a34a] text-white`}
        data-testid="status-paid"
      >
        <CheckCircle2 size={11} /> Paid
      </span>
    );
  }
  if (status === "deposit") {
    return (
      <span
        className={`${base} bg-[#005bea] text-white`}
        data-testid="status-deposit"
        title={`Deposit in: ${fmtMoney(deposits)}`}
      >
        <Wallet size={11} /> Deposit · bal {fmtMoney(balance)}
      </span>
    );
  }
  if (status === "owes") {
    return (
      <span
        className={`${base} bg-[#ff5722] text-white`}
        data-testid="status-owes"
      >
        <AlertCircle size={11} /> Owes {fmtMoney(balance)}
      </span>
    );
  }
  return (
    <span
      className={`${base} bg-zinc-200 text-zinc-700`}
      data-testid="status-draft"
    >
      Draft
    </span>
  );
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "owes", label: "Needs to pay" },
  { key: "deposit", label: "Deposit in" },
  { key: "paid", label: "Paid" },
];

const Customers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [settings, setSettings] = useState({});

  useEffect(() => {
    listCustomers()
      .then((d) => setRows(d || []))
      .finally(() => setLoading(false));
    getAppSettings()
      .then(setSettings)
      .catch(() => {});
  }, []);

  const handleRemind = (e, c) => {
    e.preventDefault();
    e.stopPropagation();
    sendBalanceReminder(c, settings);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    // Also extract just the digits — so typing "5", "mn-5", "mn-005" all match MN-005
    const digitNeedle = needle.replace(/[^0-9]/g, "");
    return rows.filter((r) => {
      if (needle) {
        const numLabel = (r.customer_number_label || "").toLowerCase();
        const hits =
          r.name.toLowerCase().includes(needle) ||
          numLabel.includes(needle) ||
          (digitNeedle &&
            String(r.customer_number || "").padStart(3, "0").includes(digitNeedle)) ||
          (r.areas || []).some((a) => (a || "").toLowerCase().includes(needle));
        if (!hits) return false;
      }
      if (filter === "all") return true;
      if (filter === "owes") return r.status === "owes" || r.status === "deposit";
      return r.status === filter;
    });
  }, [rows, q, filter]);

  // Top-line totals: how much is outstanding + how much deposit cash is in.
  const tally = useMemo(() => {
    let owed = 0;
    let depositIn = 0;
    let unpaidCount = 0;
    rows.forEach((r) => {
      owed += r.balance || 0;
      depositIn += r.deposits_paid_sum || 0;
      if (r.status === "owes" || r.status === "deposit") unpaidCount += 1;
    });
    return { owed, depositIn, unpaidCount };
  }, [rows]);

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="customers-page">
      <div className="label-xs mb-2">MODULE 06 / CUSTOMERS</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9]">
        Customers.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl">
        Every customer rolled up — quotes, deposits, invoices, balance.
      </p>

      {/* Top-line tally tiles */}
      {!loading && rows.length > 0 ? (
        <div className="mt-8 grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          <div className="bg-white p-4">
            <div className="label-xs">Outstanding</div>
            <div
              className="font-display text-2xl sm:text-3xl font-black tracking-tighter tabular-nums mt-1 text-[#ff5722]"
              data-testid="tally-outstanding"
            >
              {fmtMoney(tally.owed)}
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mt-1">
              {tally.unpaidCount} customer
              {tally.unpaidCount === 1 ? "" : "s"}
            </div>
          </div>
          <div className="bg-white p-4">
            <div className="label-xs">Deposits in</div>
            <div
              className="font-display text-2xl sm:text-3xl font-black tracking-tighter tabular-nums mt-1 text-[#005bea]"
              data-testid="tally-deposits"
            >
              {fmtMoney(tally.depositIn)}
            </div>
          </div>
          <div className="bg-white p-4">
            <div className="label-xs">Total customers</div>
            <div className="font-display text-2xl sm:text-3xl font-black tracking-tighter tabular-nums mt-1">
              {rows.length}
            </div>
          </div>
        </div>
      ) : null}

      {/* Search + filter */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, number (MN-001), or area"
            className="pl-9 h-10 rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-[#09090b] font-mono"
            data-testid="customers-search"
          />
        </div>
        <div
          className="inline-flex border border-zinc-300"
          data-testid="customers-filter"
        >
          {FILTERS.map((f, i) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-3 h-10 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold transition-colors ${
                i > 0 ? "border-l border-zinc-300" : ""
              } ${
                filter === f.key
                  ? "bg-[#09090b] text-white"
                  : "bg-white text-[#09090b] hover:bg-zinc-100"
              }`}
            >
              {i === 0 ? <Filter size={11} /> : null}
              {f.label}
            </button>
          ))}
        </div>
        <div className="font-mono text-xs text-zinc-500 ml-auto">
          {filtered.length} {filtered.length === 1 ? "customer" : "customers"}
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="font-mono text-xs text-zinc-400 uppercase tracking-[0.18em] py-10 text-center">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="border border-dashed border-zinc-300 p-10 text-center"
            data-testid="customers-empty"
          >
            <Users size={20} className="mx-auto text-zinc-300 mb-3" />
            <div className="font-display text-xl font-black tracking-tighter">
              {rows.length === 0 ? "No customers yet" : "No matches"}
            </div>
          </div>
        ) : (
          <ul className="border border-zinc-200 divide-y divide-zinc-100">
            {filtered.map((c) => {
              // Row tint by status — green for fully paid so unpaid jobs jump out
              const rowBg =
                c.status === "paid"
                  ? "bg-[#f0fdf4] hover:bg-[#dcfce7] border-l-4 border-l-[#16a34a]"
                  : c.status === "deposit"
                  ? "bg-[#eff6ff] hover:bg-[#dbeafe] border-l-4 border-l-[#005bea]"
                  : c.status === "owes"
                  ? "bg-[#fff7ed] hover:bg-[#ffedd5] border-l-4 border-l-[#ff5722]"
                  : "hover:bg-zinc-50 border-l-4 border-l-transparent";
              return (
              <li key={`${c.name}|${c.customer_number_override ?? "default"}`}>
                <Link
                  to={
                    c.customer_number_override
                      ? `/customers/${encodeURIComponent(c.name)}?override=${c.customer_number_override}`
                      : `/customers/${encodeURIComponent(c.name)}`
                  }
                  className={`flex items-center justify-between gap-4 p-5 transition-colors ${rowBg}`}
                  data-testid={`customer-${c.name}-${c.customer_number_override ?? "def"}`}
                >
                  {c.customer_number_label ? (
                    <div
                      className="flex-none flex flex-col items-center justify-center bg-[#09090b] text-white font-mono text-[11px] tracking-[0.12em] font-bold px-3 py-2 min-w-[68px]"
                      data-testid={`customer-number-${c.name}`}
                    >
                      <span className="text-zinc-400 text-[8px] uppercase">No.</span>
                      <span className="tabular-nums">{c.customer_number_label}</span>
                    </div>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-display text-2xl font-black tracking-tighter truncate">
                        {c.name}
                      </div>
                      <StatusPill
                        status={c.status}
                        balance={c.balance}
                        deposits={c.deposits_paid_sum}
                      />
                    </div>
                    <div className="font-mono text-[11px] text-zinc-500 mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                      <span>
                        {c.quotes_count} quote
                        {c.quotes_count === 1 ? "" : "s"}
                      </span>
                      {c.deposits_paid_sum > 0 && c.status !== "paid" ? (
                        <span className="text-[#005bea]">
                          deposit {fmtMoney(c.deposits_paid_sum)}
                        </span>
                      ) : null}
                      {c.invoices_issued > 0 ? (
                        <span className="text-[#0f766e] inline-flex items-center gap-1">
                          <FileText size={10} />
                          {c.invoices_issued} invoice
                          {c.invoices_issued === 1 ? "" : "s"}
                        </span>
                      ) : null}
                      {c.areas.length ? <span>{c.areas.join(", ")}</span> : null}
                    </div>
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="label-xs">Total quoted</div>
                    <div className="font-display text-2xl font-black tracking-tighter tabular-nums">
                      {fmtMoney(c.subtotal_sum)}
                    </div>
                  </div>
                  {c.balance > 0 &&
                  (c.status === "owes" || c.status === "deposit") ? (
                    <button
                      type="button"
                      onClick={(e) => handleRemind(e, c)}
                      data-testid={`remind-${c.name}`}
                      title={`Send WhatsApp reminder for ${fmtMoney(c.balance)}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#25D366] hover:bg-[#1fb955] text-white text-[10px] uppercase tracking-[0.16em] font-semibold transition-colors"
                    >
                      <Bell size={12} /> Remind
                    </button>
                  ) : null}
                  <ChevronRight size={16} className="text-zinc-400 flex-none" />
                </Link>
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Customers;
